import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Play, Stop, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import WashCounter from "@/components/WashCounter";
import NewBookingButton from "@/components/NewBookingButton";

const DEPARTMENTS = ["Housekeeping", "Küche", "Verwaltung", "Guest Relation", "Hausmeister"];
const DEPARTMENT_EMOJIS = { "Housekeeping": "🧹", "Küche": "🍳", "Verwaltung": "📋", "Guest Relation": "🤝", "Hausmeister": "🔧" };

const liveMinutes = (isoStart) => {
  if (!isoStart) return 0;
  try {
    const ms = new Date() - new Date(isoStart);
    return Math.max(0, Math.floor(ms / 60000));
  } catch { return 0; }
};
const fmtHM = (min) => {
  const h = Math.floor((min || 0) / 60);
  const m = (min || 0) % 60;
  return `${h} Std. ${String(m).padStart(2, "0")} Min.`;
};
const fmtHMShort = (min) => {
  const h = Math.floor((min || 0) / 60);
  const m = (min || 0) % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")} Std.`;
};

/**
 * Prominent oben auf dem Dashboard: Dienst starten/beenden Button + Live-Dauer.
 */
export function DashboardTimeTracker() {
  const { user } = useAuth();
  const [active, setActive] = useState(null); // {clock_in, ...} oder {}
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);

  // User-Abteilungen aus Profil (/auth/me) für Picker + Auto-Default
  const [myDepts, setMyDepts] = useState(null);
  useEffect(() => {
    api.get("/auth/me")
      .then(r => setMyDepts((r.data?.departments || []).filter(d => DEPARTMENTS.includes(d))))
      .catch(() => setMyDepts([]));
  }, []);

  const load = () => api.get("/time-entries/active").then(r => setActive(r.data || {})).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(iv);
  }, []);

  const start = async (dept) => {
    setBusy(true);
    try {
      await api.post("/time-entries/start", { notes: "", department: dept || null });
      toast.success(dept ? `Dienst gestartet · ${dept}` : "Dienst gestartet.");
      setPickerOpen(false);
      await load();
      window.dispatchEvent(new CustomEvent("msb-duty-change"));
    }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };
  const handleStartClick = () => {
    // Hat User genau EINE Abteilung → direkt starten (gleicher Flow wie Schichtplan)
    if (myDepts && myDepts.length === 1) return start(myDepts[0]);
    setPickerOpen(true);
  };
  const stop = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/time-entries/stop", { notes: "" });
      toast.success(`Dienst beendet: ${fmtHM(data.duration_minutes)}`);
      await load();
      window.dispatchEvent(new CustomEvent("msb-duty-change"));
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  const isRunning = !!active?.clock_in;
  const duration = isRunning ? liveMinutes(active.clock_in) : 0;
  void tick; // Force re-render on interval

  // Welche Abteilungen zeigen? Wenn User-Abteilungen gesetzt → nur die; sonst alle vier
  const availableDepts = (myDepts && myDepts.length > 0) ? myDepts : DEPARTMENTS;

  return (
    <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="dashboard-time-tracker">
      {isRunning ? (
        <>
          <Button onClick={stop} disabled={busy} data-testid="dash-btn-stop"
                  className="rounded-xl bg-red-600 hover:bg-red-700 text-white h-12 px-6 shadow-sm">
            <Stop size={18} weight="fill"/><span className="ml-2 font-semibold">Dienst beenden</span>
          </Button>
          <WashCounter />
          <NewBookingButton />
          <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-50 border border-emerald-200 text-sm">
            <Clock size={16} weight="duotone" className="text-emerald-700"/>
            <span className="text-emerald-800 font-medium">
              Heute gearbeitet: <span className="font-bold font-mono">{fmtHM(duration)}</span>
            </span>
            {active.department && (
              <span className="ml-2 text-[11px] bg-white text-emerald-800 px-2 py-0.5 rounded-full border border-emerald-300 font-semibold">
                {DEPARTMENT_EMOJIS[active.department]} {active.department}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {user?.name} · seit {new Date(active.clock_in).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr
          </div>
        </>
      ) : (
        <>
          <Button onClick={handleStartClick} disabled={busy} data-testid="dash-btn-start"
                  className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-6 shadow-sm">
            <Play size={18} weight="fill"/><span className="ml-2 font-semibold">Dienst starten</span>
          </Button>
          <WashCounter />
          <NewBookingButton />
          <div className="text-sm text-muted-foreground">Kein aktiver Dienst · Klicken zum Starten der Zeiterfassung</div>
        </>
      )}
      {pickerOpen && (
        <Dialog open={true} onOpenChange={(o)=>{ if (!o) setPickerOpen(false); }}>
          <DialogContent className="max-w-md" data-testid="dash-dept-picker-dialog">
            <DialogHeader>
              <DialogTitle>In welcher Abteilung starten Sie?</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground -mt-2 mb-2">
              Für die Auswertung je Abteilung — kann bei Bedarf später vom Admin angepasst werden.
            </p>
            <div className="grid grid-cols-2 gap-2.5">
              {availableDepts.map(d => (
                <button key={d}
                        onClick={() => start(d)}
                        disabled={busy}
                        className="rounded-xl p-4 border-2 border-border hover:border-primary hover:bg-primary/5 transition text-left disabled:opacity-50"
                        data-testid={`dash-dept-pick-${d}`}>
                  <div className="text-3xl mb-1">{DEPARTMENT_EMOJIS[d]}</div>
                  <div className="font-semibold">{d}</div>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/**
 * Team-Status-Widget: 3 Kacheln nebeneinander, live-Status pro Person.
 */
export function TeamStatusWidget() {
  const [team, setTeam] = useState([]);
  const [tick, setTick] = useState(0);

  const load = () => api.get("/time-entries/team-status").then(r => setTeam(r.data)).catch(() => {});
  useEffect(() => {
    load();
    // 10s Live-Refresh damit Team-Kacheln zeitnah reagieren, wenn jemand startet/beendet
    const iv = setInterval(() => { setTick(t => t + 1); load(); }, 10000);
    // Auch reagieren wenn dieselbe App den eigenen Dienst startet/stoppt
    const onDutyChange = () => load();
    window.addEventListener("msb-duty-change", onDutyChange);
    return () => { clearInterval(iv); window.removeEventListener("msb-duty-change", onDutyChange); };
  }, []);
  void tick;

  if (!team.length) return null;
  return (
    <section className="msb-card" data-testid="team-status-widget">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center">
          <Clock size={20} weight="duotone" className="text-primary"/>
        </div>
        <div>
          <h2 className="text-xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Team-Status</h2>
          <p className="text-xs text-muted-foreground">Live · wer ist gerade im Dienst?</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {team.map(t => {
          const on = !!t.clock_in;
          const dur = on ? liveMinutes(t.clock_in) : 0;
          const cls = on
            ? "bg-emerald-50 border-emerald-300 shadow-sm"
            : "bg-slate-50 border-slate-200";
          return (
            <div key={t.user_id} className={`rounded-xl border-2 p-4 transition ${cls}`} data-testid={`team-tile-${t.user_id}`}>
              <div className="flex items-center gap-2">
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${on ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`}/>
                <div className="font-bold text-base" style={{fontFamily:"'Work Sans',sans-serif"}}>{t.name}</div>
              </div>
              <div className={`mt-2 text-sm ${on ? "text-emerald-800" : "text-muted-foreground"}`}>
                {on
                  ? <>Dienstzeit: <span className="font-mono font-bold">{fmtHMShort(dur)}</span></>
                  : <span className="italic">Außer Dienst</span>}
              </div>
              {on && (
                <div className="text-[11px] text-emerald-700 mt-0.5">
                  seit {new Date(t.clock_in).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
